/**
 * Maps the anonymous homepage 6Q (`swtpa_diag_v1`) onto the authenticated
 * user-profile callables (`createTrialUser` / `updateUserProfile`).
 *
 * captureLead still writes `leads/{hash}` for logged-out Pass Plan / nurture.
 * After Firebase auth, this snapshot must ALSO land on `users/{uid}` so the
 * engine (and web dashboard) can read `profile.diagnostic`.
 *
 * The field types come from `@/types/backendCallables`, mirrored from
 * `aswb-backend/api-contracts/openapi.yaml`.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { TestType } from '@/models/SharedTypes';
import { logger } from '@/utils/logger';
import {
  DIAGNOSTIC_QUESTION_TOTAL,
  readDiagnosticResult,
  type StoredDiagnostic,
} from '@/utils/diagnosticMemory';
import type {
  CreateTrialUserRequest,
  DiagnosticSnapshotInput,
  UpdateUserProfileRequest,
} from '@/types/backendCallables';

/** Fields createTrialUser accepts in addition to the existing signup payload. */
export type TrialUserDiagnosticFields = Pick<
  CreateTrialUserRequest,
  'diagnostic' | 'name' | 'examDate'
>;

/** Fields updateUserProfile accepts when copying the 6Q onto an existing user. */
export type UpdateUserProfileDiagnosticFields = Required<
  Pick<UpdateUserProfileRequest, 'diagnostic'>
> &
  Pick<UpdateUserProfileRequest, 'targetExam' | 'name' | 'onboardingCompleted'>;

/** Convert local diagnostic memory into the callable `diagnostic` object. */
export function toUserDiagnosticPayload(stored: StoredDiagnostic): DiagnosticSnapshotInput {
  const score = stored.correctCount;
  const payload: DiagnosticSnapshotInput = {
    score,
    total: DIAGNOSTIC_QUESTION_TOTAL,
    readiness: Math.round((score / DIAGNOSTIC_QUESTION_TOTAL) * 100),
    weakAreas: [...stored.weak],
    examType: stored.examType,
    completedAt: new Date().toISOString(),
  };
  const completed = new Date(stored.completedAt);
  if (Number.isFinite(completed.getTime())) {
    payload.completedAt = completed.toISOString();
  }
  return payload;
}

function trimmedName(name?: string | null): string | undefined {
  const value = name?.trim();
  return value ? value : undefined;
}

/**
 * Homepage 6Q stores LCSW/LMSW/LBSW; profile.targetExam is the TestType enum.
 * An exam type outside the enum is dropped rather than lowercased through: the
 * contract rejects an unknown targetExam, which would lose the diagnostic too.
 */
function toTargetExam(examType: string | null): TestType | undefined {
  if (!examType) return undefined;
  const track = examType.toLowerCase();
  return (Object.values(TestType) as string[]).includes(track)
    ? (track as TestType)
    : undefined;
}

function futureIsoExamDate(value?: string | null): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getTime() <= Date.now()) return undefined;
  return date.toISOString();
}

/**
 * Extra fields to spread into `createTrialUser`. Diagnostic omitted when the
 * visitor never finished the six. No `onboardingCompleted`: createTrialUser
 * derives it from the diagnostic plus a known exam track and ignores a client
 * value (aswb-backend/src/createTrialUser.ts).
 */
export function trialUserDiagnosticFields(opts?: {
  name?: string | null;
  examDate?: string | null;
}): TrialUserDiagnosticFields {
  const stored = readDiagnosticResult();
  const diagnostic = stored ? toUserDiagnosticPayload(stored) : undefined;
  const name = trimmedName(opts?.name);
  const examDate = futureIsoExamDate(opts?.examDate);
  return {
    ...(diagnostic ? { diagnostic } : {}),
    ...(name ? { name } : {}),
    ...(examDate ? { examDate } : {}),
  };
}

/**
 * Payload for `updateUserProfile` when local 6Q memory exists. Null when there
 * is nothing to copy — callers skip the callable.
 */
export function updateUserProfileDiagnosticFields(opts?: {
  name?: string | null;
}): UpdateUserProfileDiagnosticFields | null {
  const stored = readDiagnosticResult();
  if (!stored) return null;
  const diagnostic = toUserDiagnosticPayload(stored);
  const name = trimmedName(opts?.name);
  const examType = diagnostic.examType ?? null;
  const targetExam = toTargetExam(examType);
  return {
    diagnostic,
    ...(targetExam ? { targetExam } : {}),
    ...(name ? { name } : {}),
    ...(examType ? { onboardingCompleted: true } : {}),
  };
}

const PERSIST_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Diagnostic persist timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Best-effort copy of the 6Q onto `users/{uid}` via the existing
 * `updateUserProfile` callable. Never throws — sign-in must not fail because
 * the snapshot write did. No-op when there is no completed diagnostic.
 * Times out so AppleAuthComplete cannot hang on a stalled callable.
 */
export async function persistDiagnosticOnUser(opts?: {
  name?: string | null;
}): Promise<void> {
  const fields = updateUserProfileDiagnosticFields(opts);
  if (!fields) return;
  try {
    const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
    await withTimeout(updateUserProfile(fields), PERSIST_TIMEOUT_MS);
  } catch (error) {
    logger.warn('OnboardingDiagnostic', 'Failed to persist diagnostic onto user (non-fatal)', {
      error,
    });
  }
}
